import * as React from 'react'
import moment from 'moment'
import { ReactComponent as ArrowLeftIcon } from 'assets/images/XSArrowLeft.svg'
import { ReactComponent as ArrowRightIcon } from 'assets/images/XSArrowRight.svg'
import {
  DatepickerBaseContainer,
  DatepickerSwitchHeader,
  DatepickerDayLabel,
  DatepickerWeek,
  DatepickerDayButton,
  DatepickerDayButtonSecondary,
  DatepickerDaysContainer,
  DatepickerActions,
  DatepickerButtonCancel,
  DatepickerButtonAccept,
  DatepickerDate,
  Icon,
} from './Styles'
import { formBreakpoint } from 'globalStyles'
import { useMedia } from 'utils/hooks'

type Props = {
  show: boolean
  inputValue: moment.Moment | null
  handleSubmit: (selectedDate: moment.Moment) => void
  handleCancel: () => void
  selectedDate: moment.Moment | null
  setSelected: React.Dispatch<React.SetStateAction<moment.Moment | null>>
}

export const MAX_PREVIOUS_MONTHS = 2
export const MAX_NEXT_MONTHS = 4

const DatePicker: React.FC<Props> = ({
  show,
  inputValue,
  handleSubmit,
  handleCancel,
  setSelected,
  selectedDate,
}) => {
  const [date, setDate] = React.useState<moment.Moment>(() =>
    inputValue ? moment(inputValue) : moment()
  )
  const [minMonth, maxMonth] = React.useMemo(() => {
    const prevMonth = moment(inputValue ? inputValue : undefined).subtract(
      MAX_PREVIOUS_MONTHS,
      'month'
    )
    const nextMonth = moment(inputValue ? inputValue : undefined).add(MAX_NEXT_MONTHS, 'month')
    return [prevMonth, nextMonth]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const isMobile = useMedia(`(max-width:${formBreakpoint})`)

  if (!show) return null

  const preventDefault = (cb: () => void) => (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    cb()
  }

  const handleClickAccept = preventDefault(handleSubmit.bind(this, selectedDate!))
  const handleClickCancel = preventDefault(handleCancel)
  const handleClickArrow = (dir: number) => {
    setDate((prev) => {
      const nextState = moment(prev).add(dir, 'month')
      if (!nextState.isBetween(minMonth, maxMonth)) return prev
      return nextState
    })
  }
  const handleClickDay = (d: moment.Moment) => {
    setSelected(d)
    if (isMobile) handleSubmit(d)
  }

  const prevMonthDate = moment(date).clone().subtract(1, 'month')
  const nextMonthDate = moment(date).clone().add(1, 'month')

  const weekDayLabels = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
  const isCurrentDay = (customDate: moment.Moment, day: number) => {
    const currentDate = moment()
    return day === currentDate.date() && customDate.month() === currentDate.month()
  }

  const renderPrevDays = (i: number, blockDays: boolean) => (day: number) => (
    <DatepickerDayButtonSecondary
      key={i++}
      onClick={preventDefault(handleClickDay.bind(this, prevMonthDate.clone().set('date', day)))}
      current={isCurrentDay(prevMonthDate, day)}
      selected={
        !selectedDate
          ? false
          : selectedDate.date() === day && selectedDate.month() === prevMonthDate.month()
      }
      blockDay={blockDays}
    >
      {day}
    </DatepickerDayButtonSecondary>
  )
  const renderCurrDays = (i: number) => (day: number) => (
    <DatepickerDayButton
      key={i++}
      onClick={preventDefault(handleClickDay.bind(this, moment(date).clone().set('date', day)))}
      current={isCurrentDay(date, day)}
      selected={
        !selectedDate ? false : selectedDate.date() === day && selectedDate.month() === date.month()
      }
    >
      {day}
    </DatepickerDayButton>
  )
  const renderNextDays = (i: number, blockDays: boolean) => (day: number) => (
    <DatepickerDayButtonSecondary
      key={i++}
      onClick={preventDefault(handleClickDay.bind(this, nextMonthDate.clone().set('date', day)))}
      current={isCurrentDay(nextMonthDate, day)}
      selected={
        !selectedDate
          ? false
          : selectedDate.date() === day && selectedDate.month() === nextMonthDate.month()
      }
      blockDay={blockDays}
    >
      {day}
    </DatepickerDayButtonSecondary>
  )

  const weekCounts = () => {
    const firstDayOfMonth = date.startOf('month').day()
    const lastDayOfMonth = date.endOf('month').day()
    const blockNextDays = date.month() === maxMonth.clone().subtract(1, 'month').month()
    const blockPrevDays = date.month() === minMonth.month()
    const startDay = firstDayOfMonth === 0 ? 7 : firstDayOfMonth
    const prevMonthDays = prevMonthDate.daysInMonth()
    const prevDays =
      startDay === 1
        ? []
        : Array.from({ length: startDay - 1 }, (_, i) => prevMonthDays - i).reverse()
    const lastDay = lastDayOfMonth === 0 ? 7 : lastDayOfMonth
    const nextDays = lastDay === 7 ? [] : Array.from({ length: 7 - lastDay }, (_, i) => i + 1)
    const days = Array.from({ length: moment(date).daysInMonth() }, (_, i) => i + 1)

    return [
      ...prevDays.map(renderPrevDays(0, blockPrevDays)),
      ...days.map(renderCurrDays(prevDays.length)),
      ...nextDays.map(renderNextDays(days.length + prevDays.length, blockNextDays)),
    ]
  }
  const chunkIntoDatepickerweek = (arr: any[]) => {
    const len = arr.length / 7
    const chunks = Array(len)
      .fill(0)
      .map((_, i) => {
        return <DatepickerWeek key={i}>{arr.slice(i * 7, i * 7 + 7)}</DatepickerWeek>
      })
    return chunks
  }

  return (
    <DatepickerBaseContainer>
      <DatepickerSwitchHeader>
        <Icon onClick={() => handleClickArrow(-1)}>
          <ArrowLeftIcon />
        </Icon>
        <DatepickerDate>{moment(date).format('MMMM yyyy')}</DatepickerDate>
        <Icon onClick={() => handleClickArrow(1)}>
          <ArrowRightIcon />
        </Icon>
      </DatepickerSwitchHeader>
      <DatepickerDaysContainer>
        <DatepickerWeek>
          {weekDayLabels.map((label, i) => (
            <DatepickerDayLabel key={i}>{label}</DatepickerDayLabel>
          ))}
        </DatepickerWeek>
        {chunkIntoDatepickerweek(weekCounts())}
      </DatepickerDaysContainer>
      {!isMobile && (
        <DatepickerActions>
          <DatepickerButtonCancel onClick={handleClickCancel}>Cancel</DatepickerButtonCancel>
          <DatepickerButtonAccept onClick={handleClickAccept} disabled={selectedDate === null}>
            Set date
          </DatepickerButtonAccept>
        </DatepickerActions>
      )}
    </DatepickerBaseContainer>
  )
}

export default React.memo(DatePicker)
